import queryString from 'query-string';

import timedFetch from 'middleware/timedFetch';
import { trackError } from 'helpers/ErrorUtils';

const v2CustomerEndpoint = '/customer/v2/preferences';
export const ENDPOINT_OPAL_EXPLICITFITS = '/opalSizingAuth/explicit/fits';

/**
 * Get product sizing prediction
 * @param  {object}   url             sizing prediction api endpoint
 * @param  {object}   productId       product id
 * @param  {function} [fetcher=fetch] fetch or fetch like implementation
 * @return {object}                   promise
 */
export function sizingPrediction({ url }, { productId }, fetcher = timedFetch('sizingPrediction')) {

  const queryParams = {
    productId,
    ignoreCustomerForOnDemandEligible: true
  };

  const query = queryString.stringify(queryParams);
  const reqUrl = `${url}/sizing/v2/prediction?${query}`;
  return fetcher(reqUrl, {
    method: 'GET',
    credentials: 'include'
  });
}

/**
 * Get on-demand sizing prediction
 * @param  {object}   url             on-demand sizing prediction api endpoint
 * @param  {object}   reqData         productId, productSize, brandId, productCategory, gender
 * @param  {function} [fetcher=fetch] fetch or fetch like implementation
 * @return {object}                   promise
 */
export function sizingPredictionOnDemand({ url }, reqData, fetcher = timedFetch('sizingPredictionOnDemand')) {
  const { productId, productSize, brandId, productCategory, gender } = reqData;
  const queryParams = {
    productId,
    size: productSize,
    brandId,
    productCategory,
    ignoreCustomerForOnDemandEligible: true
  };

  if (gender) {
    queryParams.gender = gender;
  }

  const query = queryString.stringify(queryParams);
  const reqUrl = `${url}/sizing/v2/prediction/onDemand?${query}`;
  return fetcher(reqUrl, {
    method: 'GET',
    credentials: 'include'
  });
}

/**
 * Get a list of supported brands for on-demand sizing prediction
 * @param  {object}   url             supported brands api endpoint
 * @param  {object}   gender          gender ("Men" or "Women")
 * @param  {function} [fetcher=fetch] fetch or fetch like implementation
 * @return {object}                   promise
 */
export function supportedBrands({ url }, gender, fetcher = timedFetch('sizingSupportedBrands')) {
  const reqUrl = `${url}/sizing/supportedBrands?gender=${gender}`;
  return fetcher(reqUrl, {
    method: 'GET',
    credentials: 'include'
  });
}

/**
 * Get opal profile
 * @param  {object}   url             opal url
 * @param  {function} [fetcher=fetch] fetch or fetch like implementation
 * @return {object}                   promise
 */
export function profile({ url }, fetcher = timedFetch('profile')) {
  const reqUrl = `${url}/customer/customerProfile`;
  return fetcher(reqUrl, {
    method: 'GET',
    credentials: 'include'
  });
}

/**
 * Get the variable bias
 * @param  {object}   url             opal url
 * @param  {function} [fetcher=fetch] fetch or fetch like implementation
 * @return {object}                   promise
 */
export function variableBias({ url }, fetcher = timedFetch('variableBias')) {
  const reqUrl = `${url}/customer/variableBias`;
  return fetcher(reqUrl, {
    method: 'GET',
    credentials: 'include'
  });
}

/**
 * Save Customers filters
 * @param  {object}   opal          calypso configuration
 * @param  {string}   filters         filters object from redux state
 * @return {object}                   promise
 */
export function saveFiltersToOpal({ url }, { savedsizes, organizedFilters }, fetcher = timedFetch('saveFilters')) {
  const customerId = savedsizes?.id;
  const method = !customerId ? 'POST' : 'PUT';
  const reqUrl = `${url}${v2CustomerEndpoint}/sizeFilters${customerId ? `/${customerId}` : ''}`;
  return fetcher(reqUrl, {
    method,
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify(organizedFilters),
    credentials: 'include'
  }).catch(e => trackError('NON-FATAL', 'Could not save filters.', e));
}

export function deleteFiltersFromOpal({ url }, id, fetcher = timedFetch('deleteFilters')) {
  return fetcher(`${url}${v2CustomerEndpoint}/sizeFilters/${id}`, {
    method: 'DELETE',
    headers: {
      'Content-Type': 'application/json'
    },
    credentials: 'include'
  }).catch(e => trackError('NON-FATAL', 'Could delete saved filters.', e));
}

export function genericSizeBias({ url }, productId, fetcher = timedFetch('genericSizeBiasOpal')) {
  return fetcher(`${url}/product/v1/g/sizeBias?productId=${productId}`);
}
